import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ShieldCheckIcon } from 'lucide-react';
import type { Bundle, BundleId } from '../../data/pricing';
import { freeShippingLive, includedPerLight, includedPerLightEs, pricingConfig, systemPurpose } from '../../data/pricing';
import { productAssets } from '../../data/images';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFunnel } from '../../contexts/FunnelContext';
import { useSystemSelection } from '../../contexts/SystemSelectionContext';
import { bundleName, formatMoney, getBundle, priceBundle, startingPrice } from '../../utils/pricing';
import { buttonClasses } from '../../utils/buttonStyles';
import { track } from '../../utils/analytics';
import { WarrantyTag } from '../ui/WarrantyTag';
import { ProductRating } from '../reviews/ProductRating';
type BundleSelectorProps = {
  /** External request (e.g. hero quick-pick) to select a specific system. */
  request?: {
    id: BundleId;
    n: number;
  } | null;
};

/** All seven M1 PRO systems — every value and image derives from data/pricing.ts. */
export function BundleSelector({
  request = null
}: BundleSelectorProps) {
  const {
    t,
    lang
  } = useLanguage();
  const es = lang === 'es';
  const {
    startSystem
  } = useFunnel();
  const {
    selectedId,
    setSelectedId
  } = useSystemSelection();
  useEffect(() => {
    if (request) setSelectedId(request.id);
  }, [request, setSelectedId]);
  const bundle = getBundle(selectedId);
  const p = priceBundle(bundle);
  const m = (n: number) => formatMoney(n, lang);
  const included = es ? includedPerLightEs : includedPerLight;
  const packaging = productAssets.packaging;
  const select = (b: Bundle) => {
    setSelectedId(b.id);
    track('bundle_view', {
      bundleId: b.id,
      content_ids: [b.sku],
      qty: b.qty,
      value: priceBundle(b).total,
      currency: pricingConfig.currency
    });
  };
  const ctaLabel = bundle.id === 'sys8' || bundle.id === 'sys24' ? `${es ? 'Quiero mi' : 'Get my'} ${es ? bundle.taglineEs : bundle.tagline} — ${m(p.total)}` : `${t('common.addToCart')} — ${m(p.total)}`;
  return <div className="grid gap-8 lg:grid-cols-[1fr_1.05fr] lg:gap-14" data-id="element-743" data-mp="3ca5sqdqlj34b">
      {/* Preview — the approved artwork for the selected system. */}
      <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-744" data-mp="32dknbranzpzy">
        <div className="mf-hud relative mx-auto aspect-[1122/1402] w-full max-w-[540px] overflow-hidden border border-marine/25 bg-[#06121F]" data-id="element-745" data-mp="32f5gpdl8t2w8">
          {pricingConfig.bundles.map(b => <link key={b.id} rel="preload" as="image" href={b.image} data-id="element-746" data-mp="3v3jeptcbzz7s" />)}
          <AnimatePresence initial={false} data-id="element-747" data-mp="3q2djnov77zv3">
            <motion.img key={bundle.id} src={bundle.image} alt={`${bundleName(bundle, lang)} — ${bundle.qty} MarineForceLED M1 PRO light${bundle.qty === 1 ? '' : 's'}`} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} className="absolute inset-0 h-full w-full object-cover" data-id="element-748" data-mp="3q584vim1dopk" />
          </AnimatePresence>
        </div>
        {packaging.src && packaging.ledCountVerified && <img src={packaging.src} alt="MarineForceLED M1 PRO retail packaging" loading="lazy" className="mt-4 hidden w-full border border-white/10 object-cover lg:block" data-id="element-749" data-mp="z3hd49tnuot6" />}
      </div>

      {/* Selection + purchase */}
      <div data-id="element-750" data-mp="1dsls7i55scx2">
        <ProductRating data-id="element-751" data-mp="1dsdj81c5prr5" />
        <h3 className="mt-3 font-display text-3xl font-semibold uppercase leading-none tracking-[0.03em] md:text-4xl" data-id="element-752" data-mp="1dvrl78gwryy2">
          M1 PRO <span className="text-titanium" data-id="element-753" data-mp="1mv4vu4grqtkd">— 13 LED RGB {es ? 'Luz recargable' : 'Rechargeable Light'}</span>
        </h3>
        <p className="mt-3 text-titanium" data-id="element-754" data-mp="3jnyc2lcrdbkd">
          {es ? 'Desde' : 'Starting at'} <span className="font-semibold text-white" data-id="element-755" data-mp="3olq55ip533q5">{m(startingPrice())}</span>
          {es ? ' · cada sistema incluye el número indicado de luces M1 PRO y bases magnéticas.' : ' · every system includes the stated number of M1 PRO lights and magnetic bases.'}
        </p>

        <fieldset className="mt-6" data-id="element-756" data-mp="2of2rvzhszt15">
          <legend className="sr-only" data-id="element-757" data-mp="33a8zus0gm1x4">{es ? 'Elige tu sistema' : 'Choose your system'}</legend>
          <div className="space-y-2" data-id="element-758" data-mp="2yacsqjwgtudc">
            {pricingConfig.bundles.map(b => <OptionRow key={b.id} bundle={b} checked={b.id === selectedId} onSelect={() => select(b)} data-id="element-759" data-mp="2al205pp9r6aa" />)}
          </div>
        </fieldset>

        <div className="mt-6 border border-white/10 bg-navy p-5 md:p-6" data-id="element-760" data-mp="25o1x7nnpujne">
          <div className="flex items-end justify-between gap-4" data-id="element-761" data-mp="2kc6eqob18p4l">
            <div data-id="element-762" data-mp="2fkoek2gu2sko">
              <p className="text-sm text-titanium" data-id="element-763" data-mp="2fk4vgifg5y52">
                {bundleName(bundle, lang)} · {bundle.qty} M1 PRO
              </p>
              <p className="mt-1 font-display text-5xl font-semibold leading-none" data-id="element-764" data-mp="1rsziqc4szlyz">{m(p.total)}</p>
            </div>
            <div className="text-right text-sm" data-id="element-765" data-mp="1mtmtjkqgm2t8">
              <p className="text-titanium" data-id="element-766" data-mp="2df4ood5gs22">
                {m(p.perUnit)} {t('common.perLight')}
              </p>
              {p.savings > 0 && <p className="mt-0.5 font-semibold text-ice" data-id="element-767" data-mp="7687h6p25qbi">
                  {es ? 'Ahorras' : 'You save'} {m(p.savings)}
                </p>}
            </div>
          </div>

          <button type="button" onClick={() => startSystem(bundle.id)} className={`${buttonClasses('primary', 'lg')} mt-5 w-full`} data-id="element-768" data-mp="3thx2lf6r9qq5">
            {ctaLabel}
          </button>

          <div className="mt-5 grid gap-x-6 gap-y-2 border-t mf-hairline pt-5 text-sm text-titanium sm:grid-cols-2" data-id="element-769" data-mp="q2r97axj8by1">
            <WarrantyTag tier={bundle.warranty} linked data-id="element-770" data-mp="c5ksrn0a6pbz" />
            <p data-id="element-771" data-mp="g4bpvu4qy6z3">{freeShippingLive() ? es ? 'Envío estándar gratis en EE.UU. continental' : 'Free standard shipping, contiguous U.S.' : `${t('common.shipping')}`}</p>
            {included.map(i => <p key={i} className="flex gap-2 sm:col-span-2" data-id="element-772" data-mp="30wdkcdnqjr1o">
                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-marine" aria-hidden data-id="element-773" data-mp="17cln188e89dx" />
                <span data-id="element-774" data-mp="17eg3a98pzdbo">
                  {bundle.qty} × {i}
                </span>
              </p>)}
          </div>
        </div>
      </div>
    </div>;
}
function OptionRow({
  bundle,
  checked,
  onSelect
}: {
  bundle: Bundle;
  checked: boolean;
  onSelect: () => void;
}) {
  const {
    lang
  } = useLanguage();
  const es = lang === 'es';
  const p = priceBundle(bundle);
  const m = (n: number) => formatMoney(n, lang);
  const badge = bundle.badge === 'mostPopular' ? es ? 'Más popular' : 'Most popular' : bundle.badge === 'bestValue' ? es ? 'Mejor valor' : 'Best value' : null;
  const lifetime = bundle.warranty === 'lifetime';
  return <label className={`relative flex cursor-pointer gap-3 border p-2.5 pr-4 transition-[border-color,background-color] duration-150 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ice sm:gap-4 ${checked ? 'border-marine bg-[#0D2440]' : bundle.featured ? 'border-marine/50 bg-[#081A2E] hover:border-marine' : 'border-white/15 bg-[#06121F] hover:border-marine/50'}`} data-id="element-775" data-mp="9q4sgar1o9vv">
      <input type="radio" name="m1pro-system" value={bundle.id} checked={checked} onChange={onSelect} className="sr-only" data-id="element-776" data-mp="2gj3vyg037qbi" />
      <img src={bundle.image} alt="" loading="lazy" className="h-[72px] w-[58px] shrink-0 object-cover object-[50%_30%]" data-id="element-777" data-mp="2qbt2qxkp0g0i" />
      <span className="min-w-0 flex-1 self-center" data-id="element-778" data-mp="1tm288yceisup">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5" data-id="element-779" data-mp="1xmm4lzp5hsfs">
          <span className="font-display text-lg font-semibold uppercase leading-tight tracking-[0.03em] text-white" data-id="element-780" data-mp="1xmvvrqlxm5n2">
            {es ? bundle.taglineEs : bundle.tagline}
          </span>
          {badge && <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ice" data-id="element-781" data-mp="27m3aoi8be8ca">{badge}</span>}
        </span>
        <span className="block text-[13px] text-titanium" data-id="element-782" data-mp="3e3870vw157ee">
          {bundle.qty} M1 PRO · {m(p.perUnit)} {es ? 'por luz' : 'per light'}
          {p.savings > 0 && <>
              {' · '}
              <span className="text-ice" data-id="element-783" data-mp="3x07px6by9l3">
                {es ? 'Ahorra' : 'Save'} {m(p.savings)}
              </span>
            </>}
        </span>
        <span className={`mt-0.5 inline-flex items-center gap-1 text-[12px] ${lifetime ? 'text-white' : 'text-titanium'}`} data-id="element-784" data-mp="hpzijlv63fmb">
          <ShieldCheckIcon className={`h-3.5 w-3.5 ${lifetime ? 'text-marine' : 'text-titanium'}`} aria-hidden data-id="element-785" data-mp="cvvbz3ptd9lr" />
          {lifetime ? es ? 'Garantía de por vida' : 'Lifetime warranty' : es ? 'Garantía de 1 año' : '1-year warranty'}
        </span>
        {checked && <span className="mt-1.5 block text-[13px] leading-snug text-titanium" data-id="element-786" data-mp="10lzfzc6wzbda">{systemPurpose[bundle.id][es ? 'es' : 'en']}</span>}
      </span>
      <span className="shrink-0 self-center text-right font-display text-xl font-semibold text-white" data-id="element-787" data-mp="vmd353uzzx50">{m(p.total)}</span>
    </label>;
}